import * as React from 'react';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { DropdownButton, DropdownButtonProps, DropdownButtonType } from '../Dropdown/DropdownButton';
import { ColorGridWidget, ColorGridWidgetProps } from './ColorGridWidget';
import { ColorGridPreset, isColorInPreset } from './ColorPickerPresets';
import { CustomColorInput } from './CustomColorInputWidget';

export interface ColorPickerWidgetProps extends
  Pick<DropdownButtonProps, 'disabled' | 'focusOnClose' | 'label' | 'popupAlignment' | 'stackingRootId' | 'zIndex'>,
  Pick<ColorGridWidgetProps, 'colorsPreset' | 'selectedColor'> {

  /** @deprecated use `onColorSelected` */ handleColorSelected?: (color: string) => void;
  /** @deprecated use `focusOnClose` */ shouldReturnFocusOnClose?: boolean;
  /** @deprecated use `zIndex` */ dropdownZIndex?: number;

  /**
   * The width of the color ribbon that will appear in the button
   */
  colorLabelWidth?: number;

  /**
   * Callback function to be called when a color is picked from the grid
   */
  onColorSelected: (color: string) => void;

  /**
   * whether or not to show the custom color input widget at the bottom of the color picker
   */
  showCustomColorInput?: boolean;

  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix `'-ColorPicker'` will be automatically added
   */
  testId: string;

  /**
   * Tooltip text to be shown by the browser when user hovers the element
   */
  title?: string;
}

function getCustomColorInput(selectedColor: string | undefined, colorsPreset: ColorGridPreset): string {
  if (!selectedColor) { return ''; }
  return !isColorInPreset(selectedColor, colorsPreset) ? selectedColor : '';
}

export const ColorPickerWidget = React.forwardRef<DropdownButton, ColorPickerWidgetProps>((props, ref) => {
  const dialogId = React.useState(generateId())[0];
  const closeFn = React.useRef<(() => void) | null>(null);

  const onColorSelected = (color: string) => {
    closeFn.current?.();
    // eslint-disable-next-line deprecation/deprecation
    (props.onColorSelected || props.handleColorSelected)?.(color.toLowerCase());
  };

  const selectedColorString = (props.selectedColor || '').toLowerCase();

  const getElementToFocus: DropdownButtonProps['getElementToFocus'] =
    props.showCustomColorInput && props.selectedColor && !isColorInPreset(props.selectedColor, props.colorsPreset!) &&
    (popup => popup.querySelector('input')!) || undefined;

  return <div style={{ display: 'inline-block' }} data-tb-test-id={makeTestId(props.testId, 'ColorPicker')}>
    <DropdownButton
      aria-controls={dialogId}
      aria-haspopup='dialog'
      data-tb-test-id={makeTestId(props.testId, 'ColorPickerButton')}
      disabled={props.disabled}
      // eslint-disable-next-line deprecation/deprecation
      focusOnClose={props.focusOnClose || props.shouldReturnFocusOnClose}
      getElementToFocus={getElementToFocus}
      label={props.label}
      onOpen={doneFn => closeFn.current = doneFn}
      popupAlignment={props.popupAlignment}
      popupContent={<ColorGridPopupContent
        colorsPreset={props.colorsPreset}
        disabled={props.disabled}
        handleSwatchClicked={onColorSelected}
        id={dialogId}
        selectedColor={props.selectedColor}
        showCustomColorInput={props.showCustomColorInput}
        testId={props.testId}
      />}
      ref={ref}
      // eslint-disable-next-line deprecation/deprecation
      stackingRootId={props.stackingRootId}
      testId={null!} // overridden by data-tb-test-id above
      title={props.title}
      type={DropdownButtonType.Outline}
      // eslint-disable-next-line deprecation/deprecation
      zIndex={props.zIndex || props.dropdownZIndex}
    >
      <div aria-label={selectedColorString} style={{
        backgroundColor: selectedColorString,
        boxSizing: 'border-box',
        display: 'inline-block',
        height: '50%',
        width: props.colorLabelWidth || 40,
      }} />
    </DropdownButton>
  </div >;
});
ColorPickerWidget.defaultProps = { colorsPreset: ColorGridPreset.Dark, focusOnClose: true };
ColorPickerWidget.displayName = 'ColorPickerWidget';

type ColorGridPopupProps = ColorGridWidgetProps & { id: string, showCustomColorInput?: boolean };
const ColorGridPopupContent: React.FC<ColorGridPopupProps> = ({ id, showCustomColorInput, ...props }) =>
  <div
    data-tb-test-id={makeTestId(props.testId, 'ColorGridWrapper')}
    id={id}
    role='dialog'
    style={{ padding: TabStyles.Spacing.LargeSpace }}
  >
    <ColorGridWidget {...props} />
    {showCustomColorInput && <>
      <hr style={{ marginTop: TabStyles.Spacing.LargeSpace }} />
      <CustomColorInput
        color={getCustomColorInput(props.selectedColor, props.colorsPreset!)}
        onColorSelected={props.handleSwatchClicked}
        testId={props.testId!}
      />
    </>}
  </div>;
ColorGridPopupContent.displayName = 'ColorGridPopupContent';
ColorGridPopupContent.defaultProps = { selectedColor: '' };
